/**
 * ================================================================================================
 *  ONE BAR, IN ONE PLACE, ON EVERY SCREEN. **Section 1, 2026-10-27.**
 * ================================================================================================
 *
 * Owner: *"יש להשתמש ברכיב מעטפת משותף, ולא למקם את הסרגל מחדש בכל מסך"*, and in answer 2 of
 * 2026-10-27: *"חזרה: הכפתור יהיה בקצה השמאלי הפיזי של הסרגל בשתי השפות, במיקום ובגובה קבועים,
 * בנפרד מאזור הלשוניות."*
 *
 * This component is that bar. `AppShell` renders it at ONE point in the content region — above the
 * heading, below the toolbar — so its vertical position and its height are properties of the shell
 * and not of whichever screen happens to be inside it.
 *
 * ── WHAT WAS WRONG BEFORE, EXACTLY ──────────────────────────────────────────────────────────────
 *
 * `backToParent` was drawn by `ContextBar`, inside `.context-bar__controls`, BESIDE THE PERIOD
 * SELECTOR. So the button moved whenever the period selector appeared or vanished (09 §5.2 makes it
 * absent on a screen that is not period-bound), and again with the width of the breadcrumb beside
 * it. That is the *"פעולות מחליפות מיקום"* in the Owner's first sentence, and it was not a styling
 * accident: the control was in a container whose contents legitimately change per screen.
 *
 * It is now the FIRST child of a bar that contains nothing else but the tabs, which is what makes
 * *"במיקום ובגובה קבועים"* a structural fact rather than a promise.
 *
 * ── THE PHYSICAL LEFT, IN BOTH LANGUAGES, WITHOUT A HARD-CODED `left` ───────────────────────────
 *
 * The bar itself is laid out `dir="ltr"`, so its first child is at the physical left END of the row
 * whichever language the product is in. The TABS keep the document's own direction, on their own
 * element — Hebrew tabs read right-to-left inside their box, which sits to the right of the button.
 *
 * This is the opposite decision from `RecentlyUsed`, and deliberately so. That panel is a grid
 * TRACK and follows the inline direction, because it keeps a RELATIONSHIP (*"navigation one side,
 * recents the other"*). Here the Owner asked for a physical side, in both languages, by name — so
 * the one thing that must not happen is the layout mirroring with the locale.
 *
 * `position: static` and plain flow: a `position: absolute` button would hold the same coordinates
 * and overlap the tabs the moment there were enough of them to reach it, which is the *"ללא תלות
 * במספר הלשוניות או באורך שמותיהן"* requirement failing silently at some width nobody tested.
 *
 * ── THE BUTTON'S WIDTH DOES NOT DEPEND ON WHERE IT GOES ─────────────────────────────────────────
 *
 * It reads `חזרה` and nothing else. The destination's name is its accessible name and its tooltip —
 * *"חזרה לכרטיס הלקוח"* — so nothing is lost, while a short destination and a long one produce a
 * button of the same size in the same place. The old control printed the name, which is why it was
 * a different width on every screen that used it.
 *
 * ── WHAT THIS COMPONENT DOES NOT DO ─────────────────────────────────────────────────────────────
 *
 * It does not decide WHERE חזרה goes. The screen does, through `backToParent`, because that is
 * context the shell cannot know — *"חזרה תשיב למסך הקודם בהקשר הנכון, עם בחירות וסינון ככל
 * שרלוונטי"*. What moved into the shell is the PLACEMENT, which is the part that was inconsistent.
 *
 * It is also not `TabStrip`. That component switches content inside one record and writes a hash;
 * these tabs are LINKS between screens of an area. Two different jobs, and merging them would make
 * a record card's tabs navigate away from the record.
 */

import { matchPath } from 'react-router-dom';

import { DIRECTION } from '../i18n/catalogue';
import { useLocale } from '../i18n/LocaleProvider';
import { areaForPath, visibleTabs } from './areas';
import type { Surface } from './destinations';

export interface AreaBarProps {
  /** The address being shown. Read by the shell from the browser, once, per navigation. */
  readonly currentPath: string;
  readonly surface: Surface | null;
  readonly capabilities: readonly string[];
  /** The screen's own answer about where חזרה goes, or absent when there is nowhere above. */
  readonly backToParent?: { readonly labelHe: string; readonly href: string };
}

/** `matchPath` returns the match object or null; the area table only needs the boolean. */
function matches(pattern: string, pathname: string): boolean {
  return matchPath(pattern, pathname) !== null;
}

export function AreaBar({
  currentPath,
  surface,
  capabilities,
  backToParent,
}: AreaBarProps): JSX.Element | null {
  const { t, locale } = useLocale();

  const found = areaForPath(currentPath, matches);
  /*
   * THE SURFACE IS CHECKED, not assumed from the address. A platform session on a tenant address
   * has a session whose capabilities would decide the tabs, and drawing a customer's area strip on
   * the platform console would be a second answer to *"which surface am I on"* — the question
   * `ContextBar` and `ShellNav` already answer from the session alone.
   */
  const area = found !== null && found.area.surface === surface ? found.area : null;
  const tabs = area === null ? [] : visibleTabs(area, capabilities);

  /*
   * A STRIP OF ONE TAB IS NOT A STRIP. It would be a label that looks like navigation, and the
   * Owner asked for sub-topics — so the bar then carries חזרה alone, in the same place.
   *
   * With neither, there is nothing to draw: on the home screen of either tree the rail IS the
   * navigation and there is nothing above it to go back to.
   */
  if (tabs.length < 2 && backToParent === undefined) return null;

  return (
    /*
     * `dir="ltr"` is the mechanism for the physical left, and it is scoped to this element: the
     * tabs below restore the document's direction for their own contents.
     */
    <div className="gp-areabar" dir="ltr">
      {/*
        THE SLOT IS ALWAYS HERE, even when this screen has nowhere to go back to. It is what holds
        the tabs at the same horizontal start on every screen of the area — otherwise the strip
        would shift sideways the moment a screen without a parent was opened, which is the same
        complaint one axis over.
      */}
      <div className="gp-areabar__back">
        {backToParent === undefined ? null : (
          <a
            className="gp-areabar__backlink"
            href={backToParent.href}
            dir={DIRECTION[locale]}
            /*
              THE DESTINATION IS THE ACCESSIBLE NAME. `aria-label` replaces the visible word for a
              screen reader, so it must CONTAIN it: *"חזרה ל…"* is the translated sentence plus the
              screen's own authored name, exactly as the old control printed it.
            */
            aria-label={`${t('context.backTo')}${backToParent.labelHe}`}
            title={`${t('context.backTo')}${backToParent.labelHe}`}
          >
            {/* Decorative, and it points the way reading goes in THIS language. */}
            <span aria-hidden="true">{locale === 'he' ? '→' : '←'}</span>
            <span>{t('shell.back')}</span>
          </a>
        )}
      </div>

      {tabs.length < 2 || area === null ? null : (
        <nav
          className="gp-areabar__tabs"
          dir={DIRECTION[locale]}
          aria-label={t(area.label)}
        >
          {tabs.map((tab) => {
            const current = tab.id === found?.currentTabId;
            return (
              <a
                key={tab.id}
                className="gp-areabar__tab"
                href={tab.href}
                /*
                  LINKS, NOT `role="tab"`. These navigate to another address, and a tablist promises
                  a keyboard model — arrows moving between panels on one page — that would be a lie
                  here. `aria-current="page"` is the correct statement: one of these IS the page.
                */
                {...(current ? { 'aria-current': 'page' as const } : {})}
              >
                {t(tab.label)}
              </a>
            );
          })}
        </nav>
      )}
    </div>
  );
}
